import { Campaign, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useRef, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAppLocaleContext } from './locale_provider';

export type CampaignContextType = {
  items: Campaign[];
  fetchData: (top: number, categoryId?: string, options?: CampaignListParams) => Promise<ServiceResponse<Campaign[]>>;
  loadMore: () => Promise<ServiceResponse<Campaign[]>>;
  isLoading: boolean;
  canLoadMore: boolean;
  error: ErrorResponse | null;
};

export type CampaignListParams = {
  startDate?: string;
  keyword?: string;
  sponsorId?: string;
  maxPoints?: number;
  minPoints?: number;
  sortBy?: string;
  hashTags?: string;
  locationAgencyId?: string;
  options?: {
    [key: string]: string;
  };
};

export const CampaignContext = createContext({
  items: [],
  fetchData: async () => ({ type: 'client-error', message: 'Not implemented' }),
  loadMore: async () => ({ type: 'client-error', message: 'Not implemented' }),
  isLoading: false,
  canLoadMore: false,
  error: null,
} as CampaignContextType);

export function useCampaignContext() {
  return useContext(CampaignContext);
}

export function CampaignProvider({ children, config }: { children: React.ReactNode; config: string }) {
  const [items, setItems] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const [canLoadMore, setCanLoadMore] = useState(false);

  const currentCategoryId = useRef<string>('');
  const currentTop = useRef<number>(25);
  const currentSkip = useRef<number>(0);
  const currentFetchOptions = useRef<CampaignListParams>({});

  const { bzbsService } = useBuzzebeesServiceContext();
  const { localeId } = useAppLocaleContext();

  const fetchData = async (
    top: number,
    categoryId?: string,
    options?: CampaignListParams
  ): Promise<ServiceResponse<Campaign[]>> => {
    currentCategoryId.current = categoryId || '';
    currentFetchOptions.current = options || {};
    currentTop.current = top;
    currentSkip.current = 0;

    setIsLoading(true);
    const response = await bzbsService.campaignApi.campaigns({
      config: config,
      cat: categoryId,
      byConfig: true,
      top: currentTop.current,
      skip: currentSkip.current,
      startDate: currentFetchOptions.current.startDate,
      keyword: currentFetchOptions.current.keyword,
      sponsorId: currentFetchOptions.current.sponsorId,
      maxPoints: currentFetchOptions.current.maxPoints?.toString(),
      minPoints: currentFetchOptions.current.minPoints?.toString(),
      sortBy: currentFetchOptions.current.sortBy,
      hashTags: currentFetchOptions.current.hashTags,
      locationAgencyId: currentFetchOptions.current.locationAgencyId,
      locale: localeId,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setItems(response.model);
      setCanLoadMore(response.model.length === currentTop.current);
    } else {
      setError(response);
    }

    return response;
  };

  const loadMore = async (): Promise<ServiceResponse<Campaign[]>> => {
    if (isLoading) return { type: 'client-error', message: 'Loading' };
    if (!canLoadMore) return { type: 'client-error', message: 'No more data to load' };

    setIsLoading(true);
    currentSkip.current += currentTop.current;
    const response = await bzbsService.campaignApi.campaigns({
      config: config,
      cat: currentCategoryId.current,
      byConfig: true,
      top: currentTop.current,
      skip: currentSkip.current,
      startDate: currentFetchOptions.current.startDate,
      keyword: currentFetchOptions.current.keyword,
      sponsorId: currentFetchOptions.current.sponsorId,
      maxPoints: currentFetchOptions.current.maxPoints?.toString(),
      minPoints: currentFetchOptions.current.minPoints?.toString(),
      sortBy: currentFetchOptions.current.sortBy,
      hashTags: currentFetchOptions.current.hashTags,
      locationAgencyId: currentFetchOptions.current.locationAgencyId,
    });

    if (response.type === 'success') {
      setItems([...items, ...response.model]);
      setError(null);
      setCanLoadMore(response.model.length === currentTop.current);
    } else {
      setError(response);
    }

    setIsLoading(false);

    return response;
  };

  return (
    <CampaignContext.Provider
      value={{
        items,
        fetchData,
        loadMore,
        isLoading,
        canLoadMore,
        error,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
}
